import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FolderOpen, FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/** Files the office has shared with this user's role on a project they are on. */
export const PortalSharedFiles = ({ projectId, title = 'Shared files', emptyText = 'No files have been shared with you yet', hint = 'The office can share drawings and documents from the project folder' }) => {
  const { api } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    api.get(`/portal/projects/${projectId}/files`)
      .then(r => { if (!ignore) setFiles(r.data.files || []); })
      .catch(() => { if (!ignore) setFiles([]); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [api, projectId]);

  const openFile = async (file) => {
    try {
      const { data } = await api.get(`/portal/projects/${projectId}/files/${file.id}/download`);
      window.open(data.download_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open this file');
    }
  };

  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="portal-project-files">
      <CardHeader className="pb-2">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-blue-400" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-[#F5A623]" />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-400 text-sm" data-testid="portal-project-files-empty">{emptyText}</p>
            {hint && <p className="text-gray-500 text-xs mt-1">{hint}</p>}
          </div>
        ) : (
          <div className="space-y-2">
            {files.map(file => (
              <button
                type="button"
                key={file.id}
                onClick={() => openFile(file)}
                data-testid={`portal-file-${file.id}`}
                className="w-full flex items-center justify-between p-3 bg-[#1E2530] rounded-lg hover:bg-[#252d3a] transition-colors text-left"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-blue-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{file.name}</p>
                    <p className="text-gray-500 text-xs">{file.folder ? `${file.folder} · ` : ''}{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <Download className="w-4 h-4 text-gray-400 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
